import { HttpErrorResponse } from '@angular/common/http';
import { translate as t } from './i18n';

export function isTransientHttpError(error: unknown): error is HttpErrorResponse {
  return error instanceof HttpErrorResponse && [429, 503, 504].includes(error.status);
}

export function parseRetryAfterMs(error: unknown): number | null {
  if (!(error instanceof HttpErrorResponse)) {
    return null;
  }

  const retryAfter = error.headers?.get('Retry-After');
  if (!retryAfter) {
    return null;
  }

  const seconds = Number(retryAfter);
  if (!Number.isNaN(seconds) && Number.isFinite(seconds) && seconds >= 0) {
    return Math.round(seconds * 1000);
  }

  const parsedDate = Date.parse(retryAfter);
  if (Number.isNaN(parsedDate)) {
    return null;
  }

  return Math.max(0, parsedDate - Date.now());
}

export function buildTransientHint(error: unknown): string {
  if (!(error instanceof HttpErrorResponse)) {
    return '';
  }

  if (error.status === 429) {
    const retryAfterMs = parseRetryAfterMs(error);
    if (retryAfterMs !== null) {
      return t(
        'ui.core.transient.http.text.vuelve_a_intentarlo_en',
        { delay: formatRetryDelay(retryAfterMs) },
        `Vuelve a intentarlo en ${formatRetryDelay(retryAfterMs)}.`
      );
    }
    return t(
      'ui.core.transient.http.text.intentalo_de_nuevo_en_unos_segundos',
      undefined,
      'Inténtalo de nuevo en unos segundos.'
    );
  }

  if (error.status === 503) {
    return t(
      'ui.core.transient.http.text.servicio_temporalmente_no_disponible_intentalo_de_nuevo_en_u',
      undefined,
      'Ahora no está disponible. Intenta en un momento.'
    );
  }

  if (error.status === 504) {
    return t(
      'ui.core.transient.http.text.la_solicitud_tardo_demasiado_intentalo_de_nuevo',
      undefined,
      'Tardó demasiado. Intenta otra vez.'
    );
  }

  return '';
}

function formatRetryDelay(ms: number): string {
  const seconds = Math.max(1, Math.ceil(ms / 1000));
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.ceil(seconds / 60);
  return `${minutes}m`;
}
